import type { CompanionsProgress } from '@/types/guestOnboarding';

const STORAGE_KEY = 'sonny.onboarding.guest';

/**
 * Convidado identificado no /identify (modo evento): perfil para pré-preencher
 * o cadastro + progresso de acompanhantes para o roteamento do funil
 * (selfie → cadastro → /invite quando houver acompanhante pendente).
 */
export type OnboardingGuestState = {
  fullName: string;
  email: string | null;
  phone: string | null;
  birthDate: string | null;
  nationality: string | null;
  /** Dígitos do documento digitado no /identify (pré-preenche o cadastro). */
  documentDigits: string;
  onboardingStep: number;
  companions: CompanionsProgress;
  /** Papel no convite por link único: dependente (companion) nunca vê a tela do grupo. */
  role?: 'titular' | 'companion';
  /**
   * Convidado que JÁ tem biometria cadastrada. Bloqueia selfie/cadastro
   * (`AlreadyOnboardedGuard`) e faz o comprovante (/success) renderizar com
   * estes dados, sem depender do perfil da sessão nova — a sessão da face
   * indexada continua sendo a antiga.
   */
  alreadyOnboarded: boolean;
  /** Data (ISO) do cadastro da biometria — exibida no comprovante. */
  enrolledAt: string | null;
};

function canUseSessionStorage(): boolean {
  try {
    return typeof sessionStorage !== 'undefined';
  } catch {
    return false;
  }
}

function isCompanionsProgress(value: unknown): value is CompanionsProgress {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.declared === 'number' &&
    typeof v.captured === 'number' &&
    typeof v.pending === 'number'
  );
}

function isOnboardingGuestState(value: unknown): value is OnboardingGuestState {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.fullName === 'string' &&
    typeof v.documentDigits === 'string' &&
    typeof v.onboardingStep === 'number' &&
    isCompanionsProgress(v.companions)
  );
}

export function setOnboardingGuest(state: OnboardingGuestState): void {
  if (!canUseSessionStorage()) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Modo privado ou storage desabilitado — o funil segue só com o location state.
  }
}

/**
 * Esquece o convidado DESTE aparelho. O estado vive no `sessionStorage` da
 * aba — não é da pessoa. Num computador compartilhado, quem abre o próprio
 * convite na mesma aba em que outra pessoa já concluiu herdava o "já tem
 * biometria" dela: caía no grupo/comprovante sem nunca amarrar a própria
 * sessão, e o painel ficava parado em "Convite enviado". Toda entrada nova
 * (link pessoal diferente, URL do evento, aceite novo) começa daqui.
 */
export function clearOnboardingGuest(): void {
  if (!canUseSessionStorage()) return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getOnboardingGuest(): OnboardingGuestState | null {
  if (!canUseSessionStorage()) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isOnboardingGuestState(parsed)) return null;
    // Estado gravado antes destes campos existirem (aba aberta durante um
    // deploy) não pode virar "já cadastrado" por acidente — nem zerar as vagas
    // de acompanhante e esconder o formulário do grupo.
    return {
      ...parsed,
      companions: {
        ...parsed.companions,
        openSlots:
          typeof parsed.companions.openSlots === 'number'
            ? parsed.companions.openSlots
            : Math.max(0, parsed.companions.declared - parsed.companions.captured),
      },
      alreadyOnboarded: parsed.alreadyOnboarded === true,
      enrolledAt: parsed.enrolledAt ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Cadastro concluído: sela o convidado como "já tem biometria". A partir daqui
 * o `AlreadyOnboardedGuard` barra qualquer volta para selfie/cadastro nesta
 * sessão — sem isso, voltar no histórico depois de terminar dispara outra
 * sessão de liveness e outra indexação de face.
 */
export function markOnboardingGuestEnrolled(enrolledAt: string | null): void {
  const current = getOnboardingGuest();
  if (current === null) return;
  setOnboardingGuest({
    ...current,
    onboardingStep: 3,
    alreadyOnboarded: true,
    enrolledAt: enrolledAt ?? current.enrolledAt,
  });
}

export function updateOnboardingGuestCompanions(progress: CompanionsProgress): void {
  const current = getOnboardingGuest();
  if (current === null) return;
  setOnboardingGuest({ ...current, companions: progress });
}
